
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InputWithSuggestions } from '@/components/ui/input-with-suggestions';
import { useDebouncedQuery } from '@/hooks/useDebouncedQuery';
import { fetchPassportUnits, PassportDepartment } from '@/lib/fetchPassportUnits';
import { useToast } from '@/hooks/use-toast';

interface PassportCodeInputProps {
  code: string;
  onCodeChange: (code: string) => void;
  issuedBy: string;
  onIssuedByChange: (issuedBy: string) => void;
  showLabel?: boolean;
}

export function PassportCodeInput({
  code,
  onCodeChange,
  issuedBy,
  onIssuedByChange,
  showLabel = true
}: PassportCodeInputProps) {
  const [searchValue, setSearchValue] = useState(code);
  const { toast } = useToast();

  const debouncedSearchValue = useDebouncedQuery(searchValue, 300);

  const { data: departments = [], isLoading, error } = useQuery({
    queryKey: ['passport-departments', debouncedSearchValue],
    queryFn: () => fetchPassportUnits(debouncedSearchValue),
    enabled: debouncedSearchValue.length >= 2,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false
  });

  // Keep internal search value in sync if parent updates `code` externally
  useEffect(() => {
    if (code !== searchValue) {
      setSearchValue(code);
    }
  }, [code]);

  // Autofill 'issuedBy' when code exactly matches a department code
  useEffect(() => {
    if (!code || departments.length === 0) return;
    // Normalize both values by removing all non-digits (spaces, dashes, etc.)
    const normalize = (v: string) => (v || '').replace(/\D/g, '');
    const codeDigits = normalize(code);
    const match = departments.find(d => normalize(d.code) === codeDigits);
    if (match && issuedBy !== match.name) {
      onIssuedByChange(match.name);
    }
  }, [code, departments, issuedBy, onIssuedByChange]);

  // Guard refs to avoid repeated fallback fetches and duplicate toasts
  const lastTriedDigitsRef = useRef<string | null>(null);
  const notFoundToastShownRef = useRef<string | null>(null);

  // Fallback: if user typed full code and no match found in suggestions, do a direct lookup (once per digits)
  useEffect(() => {
    const runFallback = async () => {
      const digits = (code || '').replace(/\D/g, '');
      if (issuedBy || digits.length < 6) return;

      // If current suggestions already contain this code, skip
      const inList = departments.some(d => d.code.replace(/\D/g, '') === digits);
      if (inList) return;

      // Prevent repeated attempts for the same digits until state changes
      if (lastTriedDigitsRef.current === digits) return;
      lastTriedDigitsRef.current = digits;

      const formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}`;
      try {
        const exact = await fetchPassportUnits(formatted);
        const found = exact.find(d => d.code.replace(/\D/g, '') === digits);
        if (found) {
          if (code !== formatted) onCodeChange(formatted);
          onIssuedByChange(found.name);
          // Reset guards on success
          notFoundToastShownRef.current = null;
        } else {
          // Show one-time toast for this digits value
          if (notFoundToastShownRef.current !== digits) {
            toast({
              title: 'Подразделение не найдено',
              description: `Код ${formatted}: нет совпадений в справочнике`,
              variant: 'default'
            });
            notFoundToastShownRef.current = digits;
          }
        }
      } catch {
        // Silent fallback failure
      }
    };

    runFallback();
    // Intentionally depend on core values only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, departments, issuedBy]);

  const formatCode = (value: string) => {
    // Убираем все символы кроме цифр
    const digits = value.replace(/\D/g, '');
    
    // Форматируем в XXX-XXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}`;
    }
  };

  const handleCodeChange = (value: string) => {
    const formatted = formatCode(value);
    setSearchValue(formatted);
    onCodeChange(formatted);
  };

  const handleDepartmentSelect = (suggestion: { id: string; value: string; label: string; description?: string }) => {
    const department = departments.find(d => d.id === suggestion.id);
    if (department) {
      // Ensure selected code is consistently formatted as XXX-XXX
      const normalized = department.code.replace(/\D/g, '');
      const formatted = normalized.length >= 6
        ? `${normalized.slice(0, 3)}-${normalized.slice(3, 6)}`
        : department.code;
      onCodeChange(formatted);
      onIssuedByChange(department.name);
      setSearchValue(formatted);
      
      toast({
        title: "Подразделение найдено",
        description: `${formatted} - ${department.name}`,
      });
    }
  };

  const suggestions = departments.map(dept => ({
    id: dept.id,
    value: dept.code,
    label: dept.code,
    description: `${dept.name}${dept.region ? ` (${dept.region})` : ''}`
  }));

  if (error) {
    toast({
      title: "Ошибка загрузки",
      description: "Не удалось загрузить справочник подразделений",
      variant: "destructive",
    });
  }

  return (
    <InputWithSuggestions
      id="subdivisionCode"
      label={showLabel ? "Код Подразделения" : undefined}
      value={searchValue}
      onChange={handleCodeChange}
      onSelect={handleDepartmentSelect}
      suggestions={suggestions}
      loading={isLoading}
      placeholder="123-456"
    />
  );
}

